import * as React from "react";
import { generateUpload, putToS3, recordUpload, getVault, getDownloadUrl } from "../lib/storage";

type MediaRow = {
  id: string;
  user_id: string;
  key: string;
  bucket: string;
  content_type: string;
  bytes: number | null;
  visibility: "private" | "team" | "public";
  created_at: string;
};

export default function Vault({ userId }: { userId: string }) {
  const [items, setItems] = React.useState<MediaRow[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const { items: rows, nextCursor: nc } = await getVault(userId, cursor);
      setItems(prev => [...prev, ...rows]);
      setNextCursor(nc);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load vault");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { setItems([]); setNextCursor(null); load(); }, [load]);

  const onFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const { url, key } = await generateUpload(file, userId);
      await putToS3(url, file);
      await recordUpload(userId, key, file.type, file.size);
      // refresh from the top
      setItems([]); setNextCursor(null);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="file"
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
        {uploading && <span>Uploading…</span>}
      </div>

      {error && <div className="text-red-600">{error}</div>}

      <ul className="space-y-2">
        {items.map((m) => (
          <li key={m.id} className="flex items-center justify-between border rounded p-2">
            <div className="truncate">
              <div className="text-sm font-medium">{m.key}</div>
              <div className="text-xs opacity-70">{m.content_type} • {m.bytes ?? 0} bytes</div>
            </div>
            <button
              className="text-blue-600 underline"
              onClick={async () => {
                const { url } = await getDownloadUrl(m.key);
                window.open(url, "_blank");
              }}
            >
              View
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-2">
        {loading ? (
          <span>Loading…</span>
        ) : nextCursor ? (
          <button className="px-3 py-1 rounded border" onClick={() => load(nextCursor!)}>
            Load more
          </button>
        ) : items.length ? (
          <span className="opacity-70">End of list</span>
        ) : (
          <span className="opacity-70">No uploads yet</span>
        )}
      </div>
    </div>
  );
}
